// admin/src/lib/validators.ts
//
// Client-side mirrors of the CHECK constraints this admin writes against
// (supabase/migrations/0004_strength_core.sql, 0013_builder.sql,
// 0025_media_library.sql), so the form can reject bad input with a field
// message instead of a raw Postgres error. Each export returns `null`
// when valid, or a short user-facing message.

export const EQUIPMENT_ID_RE = /^[a-z0-9_]{2,40}$/;
export const MEDIA_SLOT_RE = /^[a-z0-9_.-]{2,80}$/;
export const BUCKET_FILE_SIZE_LIMIT_BYTES = 52_428_800; // 50 MB (0025: storage.buckets.file_size_limit)
export const MEDIA_ROW_BYTES_CAP = 26_214_400; // 25 MB (0013: exercise_media.bytes check) -- tighter than the bucket limit.

export function validateEquipmentId(id: string): string | null {
  if (!EQUIPMENT_ID_RE.test(id)) {
    return 'Id must be 2-40 characters: lowercase letters, numbers and underscores only.';
  }
  return null;
}

export function validateName(value: string, label: string, max = 80): string | null {
  const len = value.trim().length;
  if (len < 1) return `${label} is required.`;
  if (len > max) return `${label} must be ${max} characters or fewer.`;
  return null;
}

export function validateMediaSlotId(slot: string): string | null {
  if (!MEDIA_SLOT_RE.test(slot)) {
    return 'Slot id must be 2-80 characters: lowercase letters, numbers, underscore, dot or hyphen.';
  }
  return null;
}

export function validateIncrementKg(value: number): string | null {
  if (!Number.isFinite(value) || value <= 0 || value > 50) {
    return 'Increment must be greater than 0 and at most 50 kg.';
  }
  return null;
}

export function validateDescription(value: string | null, max = 2000): string | null {
  if (value && value.length > max) return `Description must be ${max} characters or fewer.`;
  return null;
}

export function validateTitle(value: string | null, max = 120): string | null {
  if (value && value.length > max) return `Title must be ${max} characters or fewer.`;
  return null;
}

export function validateFocal(value: number): string | null {
  if (!Number.isFinite(value) || value < 0 || value > 1) return 'Focal point must be between 0 and 1.';
  return null;
}

/** The bucket's own file-size limit (0025: 50 MB), with a clear message naming the number the founder will recognize. */
export function validateUploadSize(bytes: number, limit: number = BUCKET_FILE_SIZE_LIMIT_BYTES): string | null {
  if (bytes > limit) {
    const limitMb = Math.round(limit / (1024 * 1024));
    const gotMb = (bytes / (1024 * 1024)).toFixed(1);
    return `File is ${gotMb} MB; the limit is ${limitMb} MB.`;
  }
  return null;
}

/** Videos can't be compressed in the browser, and the app streams them on every exercise page view (free-tier egress). */
export const VIDEO_MAX_BYTES = 10 * 1024 * 1024;

export function validateVideoSize(bytes: number, limit: number = VIDEO_MAX_BYTES): string | null {
  if (bytes <= limit) return null;
  const limitMb = Math.round(limit / (1024 * 1024));
  const gotMb = (bytes / (1024 * 1024)).toFixed(1);
  return `Video is ${gotMb} MB. Max ${limitMb} MB: export it at 720p and try again.`;
}

const VIDEO_MIME_TYPES = new Set(['video/mp4', 'video/quicktime']);
const VIDEO_EXTENSIONS = new Set(['mp4', 'mov']);

export function validateVideoFile(file: { type?: string; name?: string }): string | null {
  const ext = file.name?.split('.').pop()?.toLowerCase() ?? '';
  const mimeOk = file.type ? VIDEO_MIME_TYPES.has(file.type) : false;
  const extOk = VIDEO_EXTENSIONS.has(ext);
  if (!mimeOk && !extOk) return 'Only .mp4 or .mov video files are allowed.';
  return null;
}

const IMAGE_MIME_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

export function validateImageFile(file: { type?: string }): string | null {
  if (file.type && !IMAGE_MIME_TYPES.has(file.type)) {
    return 'Only JPEG, PNG or WebP images are allowed.';
  }
  return null;
}
